import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import api from "../../../../api/client";
import MetricCard from "../../components/MetricCard";

const inputStyle = {
  padding: "6px 10px", border: "1px solid #d6dfed", borderRadius: 7,
  fontSize: 13, color: "#374151", outline: "none", background: "#f8fafc",
};
const thStyle = {
  padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151",
  borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f8fafc",
};
const tdStyle = { padding: "10px 16px", color: "#0f2d5c", whiteSpace: "nowrap" };

function inr(n) {
  if (n == null) return "—";
  const sign = n < 0 ? "−" : "";
  return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const pct = (n) => (n == null ? "—" : `${n}%`);
const profitColor = (n) => (n < 0 ? "#dc2626" : n > 0 ? "#15803d" : "#0f2d5c");
const lossColor = (n) => (n > 0 ? "#dc2626" : "#0f2d5c");

// Columns shared by every money table: [header, row => cell, row => colour]
const MONEY_COLUMNS = [
  ["Revenue", (r) => inr(r.revenue)],
  ["Total Cost", (r) => inr(r.totalCost)],
  ["Quality Loss", (r) => inr(r.qualityLoss), (r) => lossColor(r.qualityLoss)],
  ["Profit", (r) => inr(r.profit), (r) => profitColor(r.profit)],
  ["Margin", (r) => pct(r.marginPct)],
];

function Section({ title, note, rows, columns, rowKey, empty }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>{title}</h2>
        {note && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{note}</div>}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>{empty}</div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: 360, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {columns.map(([h]) => <th key={h} style={thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  {columns.map(([h, cell, color]) => (
                    <td key={h} style={{ ...tdStyle, color: color ? color(r) : tdStyle.color }}>{cell(r)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const incompleteMark = (r) =>
  r.complete ? null : <span title="Missing a price or cost — counted as ₹0" style={{ color: "#c2410c", marginLeft: 6 }}>⚠</span>;

export default function PnlReportTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/manufacturing/reports/pnl", {
        params: { from: dateFrom || undefined, to: dateTo || undefined, includeArchived },
      });
      setData(res.data.data);
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to load P&L report");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    const money = (r) => ({
      Revenue: r.revenue,
      "Material Cost": r.materialCost,
      "Scrap Loss": r.scrapLoss,
      "Rework Cost": r.reworkCost,
      "Total Cost": r.totalCost,
      Profit: r.profit,
      "Margin %": r.marginPct ?? "",
      Complete: r.complete ? "Yes" : "No",
    });
    const wb = XLSX.utils.book_new();
    const add = (rows, name) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{}]), name);
    add(data.byPO.map((r) => ({
      PO: r.poNumber, Customer: r.customerName, Status: r.status,
      Created: new Date(r.createdAt).toLocaleDateString("en-IN"),
      Rejected: r.rejected, Reworked: r.reworked, ...money(r), "Other Costs": r.otherCosts,
    })), "By PO");
    add(data.byCustomer.map((r) => ({ Customer: r.customerName, POs: r.poCount, ...money(r), "Other Costs": r.otherCosts })), "By Customer");
    add(data.byPart.map((r) => ({
      Part: r.partType, Code: r.partCode || "", Quantity: r.quantity, Rejected: r.rejected, Reworked: r.reworked, ...money(r),
    })), "By Part");
    add(data.byStage.map((r) => ({
      Stage: r.stageName, Rejected: r.rejected, Reworked: r.reworked,
      "Scrap Loss": r.scrapLoss, "Rework Cost": r.reworkCost, "Quality Loss": r.qualityLoss,
    })), "By Stage");
    const range = dateFrom || dateTo ? `${dateFrom || "start"}_to_${dateTo || "today"}` : "all-time";
    XLSX.writeFile(wb, `pnl-report-${range}.xlsx`);
  };

  const t = data?.totals;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#64748b" }}>POs created from</label>
          <input type="date" style={inputStyle} value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
          <label style={{ fontSize: 13, color: "#64748b" }}>to</label>
          <input type="date" style={inputStyle} value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              style={{ padding: 0, border: "none", background: "none", color: "#1d4ed8", fontSize: 13, cursor: "pointer" }}
            >
              All time
            </button>
          )}
          <label style={{ fontSize: 13, color: "#64748b", display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
            Include archived
          </label>
        </div>
        <button className="btn-primary" style={{ fontSize: 13 }} onClick={handleExport} disabled={!data?.byPO?.length}>
          Export Report
        </button>
      </div>

      {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <MetricCard label="Revenue" value={loading || !t ? "—" : inr(t.revenue)} />
        <MetricCard label="Total Cost" value={loading || !t ? "—" : inr(t.totalCost)} />
        <MetricCard label="Quality Loss" value={loading || !t ? "—" : inr(t.qualityLoss)} accent={t?.qualityLoss > 0 ? "#dc2626" : undefined} />
        <MetricCard
          label={t?.profit < 0 ? "Loss" : "Profit"}
          value={loading || !t ? "—" : `${inr(t.profit)}${t.marginPct != null ? ` · ${t.marginPct}%` : ""}`}
          accent={t ? profitColor(t.profit) : undefined}
        />
      </div>

      {!loading && t && (t.incompleteCount > 0 || data.unattributed.rejected > 0 || data.unattributed.reworked > 0) && (
        <div style={{ padding: "10px 16px", background: "#fff7ed", color: "#9a3412", fontSize: 12, border: "1px solid #fed7aa", borderRadius: 8, marginBottom: 20 }}>
          {t.incompleteCount > 0 && (
            <div>
              {t.incompleteCount} of {t.poCount} POs are missing a selling price or cost (marked ⚠). Missing values count as ₹0,
              so these totals are an estimate — fill them in on each PO's Profit &amp; Loss section.
            </div>
          )}
          {(data.unattributed.rejected > 0 || data.unattributed.reworked > 0) && (
            <div>
              {data.unattributed.rejected} rejected and {data.unattributed.reworked} reworked parts could not be matched to a part
              and are not costed. Enter them on the PO.
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : data && (
        <>
          <Section
            title={`By PO (${data.byPO.length})`}
            rows={data.byPO}
            rowKey={(r) => r.purchaseOrderId}
            empty="No POs in this range."
            columns={[
              ["PO", (r) => (
                <>
                  <Link to={`/manufacturing/po/${r.purchaseOrderId}`} style={{ color: "#1d4ed8", fontWeight: 600, textDecoration: "none" }}>{r.poNumber}</Link>
                  {incompleteMark(r)}
                </>
              )],
              ["Customer", (r) => r.customerName],
              ["Rejected", (r) => r.rejected.toLocaleString(), (r) => lossColor(r.rejected)],
              ["Reworked", (r) => r.reworked.toLocaleString()],
              ...MONEY_COLUMNS,
            ]}
          />
          <Section
            title="By Customer"
            note="Lowest profit first."
            rows={data.byCustomer}
            rowKey={(r) => r.customerName}
            empty="No customers in this range."
            columns={[
              ["Customer", (r) => <>{r.customerName}{incompleteMark(r)}</>],
              ["POs", (r) => r.poCount],
              ...MONEY_COLUMNS,
            ]}
          />
          <Section
            title="By Part"
            note="Lowest profit first. Other costs belong to the PO, so they are not included here."
            rows={data.byPart}
            rowKey={(r) => r.partCode || r.partType}
            empty="No parts in this range."
            columns={[
              ["Part", (r) => <>{r.partType}{r.partCode ? <span style={{ color: "#94a3b8" }}> · {r.partCode}</span> : null}{incompleteMark(r)}</>],
              ["Qty", (r) => r.quantity.toLocaleString()],
              ["Rejected", (r) => r.rejected.toLocaleString(), (r) => lossColor(r.rejected)],
              ["Reworked", (r) => r.reworked.toLocaleString()],
              ...MONEY_COLUMNS,
            ]}
          />
          <Section
            title="By Stage"
            note="Where parts were rejected or sent for rework, as workers reported it. Counts edited on a PO are not reflected here."
            rows={data.byStage}
            rowKey={(r) => r.stageName}
            empty="No rejects or rework reported in this range."
            columns={[
              ["Stage", (r) => r.stageName],
              ["Rejected", (r) => r.rejected.toLocaleString(), (r) => lossColor(r.rejected)],
              ["Reworked", (r) => r.reworked.toLocaleString()],
              ["Scrap Loss", (r) => inr(r.scrapLoss), (r) => lossColor(r.scrapLoss)],
              ["Rework Cost", (r) => inr(r.reworkCost), (r) => lossColor(r.reworkCost)],
              ["Quality Loss", (r) => inr(r.qualityLoss), (r) => lossColor(r.qualityLoss)],
            ]}
          />
        </>
      )}
    </div>
  );
}
